import { UserRole } from "@repo/types";

import { Platform, Action } from "../../shared";
import { AR_COLLECTIONS_FEATURES, PERMISSION_SUBJECT_FEATURES } from './features';
import {
  AR_COLLECTIONS_ROUTES,
  JUNIPER_ROUTES,
  PUBLIC_ROUTES,
  REPORTS_ROUTES,
  SETTINGS_ROUTES,
  SUPPORT_ROUTES,
} from '../../shared/constants';


// ============================================================================
// TYPE DEFINITIONS - Type aliases for permission structures
// ============================================================================

/**
 * Permission object structure with subject and action
 */
export type Permission = {
  subject: string;
  action: Action;
};

/**
 * Permissions configuration containing router and features permissions
 */
export type PermissionsConfig = {
  router: Permission[];
  features: Permission[];
};

/**
 * Platform-specific permissions for ADMIN role
 * Different platforms (XTND/LYDECKER) have different permission sets
 */
export type AdminPlatformPermissions = {
  XTND: PermissionsConfig;
  LYDECKER: PermissionsConfig;
};

/**
 * Role configuration can be either:
 * - Standard permissions (same for all platforms)
 * - Platform-specific permissions (for ADMIN role)
 */
export type RoleConfig = PermissionsConfig | AdminPlatformPermissions;

// ============================================================================
// PERMISSION SUBJECTS - Define permission subjects/objects
// ============================================================================

/**
 * Permission subjects define the resources or features that can be controlled by permissions.
 * This includes routes (navigation paths) and features (functional capabilities).
 */
export const PERMISSION_SUBJECT_ROUTES = {
  ALL: "all",
  AR_COLLECTIONS: {
    BASE: AR_COLLECTIONS_ROUTES.BASE,
    QUEUE: AR_COLLECTIONS_ROUTES.AR.COLLECTIONS,
    REPORTING: AR_COLLECTIONS_ROUTES.AR.REPORTING,
    SETTINGS: AR_COLLECTIONS_ROUTES.AR.SETTINGS,
  },
  JUNIPER_TRANSACTIONS: JUNIPER_ROUTES.TRANSACTIONS,
  JUNIPER_PDF_VIEWER: JUNIPER_ROUTES.PDF_VIEWER,
  JUNIPER_DOCUMENT_VIEWER: JUNIPER_ROUTES.DOCUMENT_VIEWER,
  REPORTS: REPORTS_ROUTES.BASE,
  SUPPORT: SUPPORT_ROUTES.FEEDBACK,
  SETTINGS: SETTINGS_ROUTES.BASE,
  TRUST_REQUESTS: "/trust-requests"
} as const;

// ============================================================================
// PERMISSION TEMPLATES - Define reusable permission templates
// ============================================================================

/**
 * Creates a permission object with a subject and action
 * @param {string} subject - The permission subject (route or feature identifier)
 * @param {Action} action - The action allowed (default: MANAGE)
 * @returns {Permission} Permission object with subject and action
 */
const createPermission = (subject: string, action: Action = Action.MANAGE): Permission => ({
  subject,
  action
});

/**
 * Permission templates for AR Collections routes
 * Provides pre-configured route permissions for different access levels
 */
const AR_COLLECTIONS_ROUTES_PERMISSIONS: {
  FULL: Permission[];
  WITHOUT_QUEUE: Permission[];
} = {
  FULL: [
    createPermission(PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.BASE),
    createPermission(PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.QUEUE),
    createPermission(PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.SETTINGS),
    createPermission(PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING),
  ],
  WITHOUT_QUEUE: [
    createPermission(PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.BASE),
    createPermission(PERMISSION_SUBJECT_ROUTES.AR_COLLECTIONS.REPORTING),
  ]
};

const ROLE_CONFIGS: Partial<Record<UserRole, RoleConfig>> = {
  /**
   * Admin role: Full permissions
   * - XTND platform: All routes and features
   * - Lydecker platform: Full AR Collections access with Write-off
   */
  [UserRole.ADMIN]: {
    XTND: {
      router: [createPermission(PERMISSION_SUBJECT_ROUTES.ALL)],
      features: [createPermission(PERMISSION_SUBJECT_FEATURES.ALL)]
    } as PermissionsConfig,
    LYDECKER: {
      router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
      features: AR_COLLECTIONS_FEATURES.FULL_ACCESS
    } as PermissionsConfig
  } as AdminPlatformPermissions,

  /**
   * Clerk: Full AR Collections access including Performance feature
   */
  [UserRole.CLERK]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
    features: AR_COLLECTIONS_FEATURES.STANDARD_ACCESS
  },

  /**
   * Billing Admin: Full AR Collections routes, standard features (no Performance)
   */
  [UserRole.BILLING_ADMIN]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
    features: AR_COLLECTIONS_FEATURES.STANDARD_ACCESS
  },

  /**
   * Bookkeeper: Same permissions as Billing Admin
   * Full AR Collections routes, standard features (no Performance)
   */
  [UserRole.BOOKKEEPER]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
    features: AR_COLLECTIONS_FEATURES.STANDARD_ACCESS
  },

  /**
   * Accounting Admin: Full AR Collections access including Performance feature
   */
  [UserRole.ACCOUNTING_ADMIN]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
    features: AR_COLLECTIONS_FEATURES.WITHOUT_WRITE_OFF
  },

  /**
   * Supervisor: Full AR Collections access including Performance feature
   */
  [UserRole.SUPERVISOR]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
    features: AR_COLLECTIONS_FEATURES.WITHOUT_WRITE_OFF
  },

  /**
   * HR Admin: Full AR Collections access including Performance feature
   */
  [UserRole.HR_ADMIN]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.FULL,
    features: AR_COLLECTIONS_FEATURES.WITHOUT_WRITE_OFF
  },

  /**
   * HR Standard: No Queue access, only Performance feature
   */
  [UserRole.HR_STANDARD]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.WITHOUT_QUEUE,
    features: AR_COLLECTIONS_FEATURES.HR_STANDARD
  },

  /**
   * Partner: Ledger Reports only, no Queue access
   */
  [UserRole.PARTNER]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.WITHOUT_QUEUE,
    features: AR_COLLECTIONS_FEATURES.LEDGER_AND_ATTY_RESPONSE
  },

  /**
   * Associate: Ledger Reports only, no Queue access
   */
  [UserRole.ASSOCIATE]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.WITHOUT_QUEUE,
    features: AR_COLLECTIONS_FEATURES.LEDGER_AND_ATTY_RESPONSE
  },

  /**
   * Assistant: Ledger Reports only, no Queue access
   */
  [UserRole.ASSISTANT]: {
    router: AR_COLLECTIONS_ROUTES_PERMISSIONS.WITHOUT_QUEUE,
    features: AR_COLLECTIONS_FEATURES.LEDGER_AND_ATTY_RESPONSE
  },
};

// ============================================================================
// PERMISSION OBJECT - Combine all role configurations by platform
// ============================================================================

/**
 * Main permission configuration object.
 * Maps platform and role combinations to their specific permissions.
 * Permissions are organized by platform (XTND, LYDECKER) and then by user role.
 */
export const PERMISSION: Record<
  Platform,
  Partial<Record<UserRole, PermissionsConfig>>
> = {
  [Platform.XTND]: {
    [UserRole.ADMIN]: (ROLE_CONFIGS[UserRole.ADMIN] as AdminPlatformPermissions)?.XTND,
    [UserRole.CLERK]: ROLE_CONFIGS[UserRole.CLERK] as PermissionsConfig,
    [UserRole.BILLING_ADMIN]: ROLE_CONFIGS[UserRole.BILLING_ADMIN] as PermissionsConfig,
    [UserRole.BOOKKEEPER]: ROLE_CONFIGS[UserRole.BOOKKEEPER] as PermissionsConfig,
    [UserRole.ACCOUNTING_ADMIN]: ROLE_CONFIGS[UserRole.ACCOUNTING_ADMIN] as PermissionsConfig,
    [UserRole.SUPERVISOR]: ROLE_CONFIGS[UserRole.SUPERVISOR] as PermissionsConfig,
    [UserRole.HR_ADMIN]: ROLE_CONFIGS[UserRole.HR_ADMIN] as PermissionsConfig,
    [UserRole.HR_STANDARD]: ROLE_CONFIGS[UserRole.HR_STANDARD] as PermissionsConfig,
    [UserRole.PARTNER]: ROLE_CONFIGS[UserRole.PARTNER] as PermissionsConfig,
    [UserRole.ASSOCIATE]: ROLE_CONFIGS[UserRole.ASSOCIATE] as PermissionsConfig,
    [UserRole.ASSISTANT]: ROLE_CONFIGS[UserRole.ASSISTANT] as PermissionsConfig,
  },

  [Platform.LYDECKER]: {
    [UserRole.ADMIN]: (ROLE_CONFIGS[UserRole.ADMIN] as AdminPlatformPermissions)?.LYDECKER,
    [UserRole.CLERK]: ROLE_CONFIGS[UserRole.CLERK] as PermissionsConfig,
    [UserRole.BILLING_ADMIN]: ROLE_CONFIGS[UserRole.BILLING_ADMIN] as PermissionsConfig,
    [UserRole.BOOKKEEPER]: ROLE_CONFIGS[UserRole.BOOKKEEPER] as PermissionsConfig,
    [UserRole.ACCOUNTING_ADMIN]: ROLE_CONFIGS[UserRole.ACCOUNTING_ADMIN] as PermissionsConfig,
    [UserRole.SUPERVISOR]: ROLE_CONFIGS[UserRole.SUPERVISOR] as PermissionsConfig,
    [UserRole.HR_ADMIN]: ROLE_CONFIGS[UserRole.HR_ADMIN] as PermissionsConfig,
    [UserRole.HR_STANDARD]: ROLE_CONFIGS[UserRole.HR_STANDARD] as PermissionsConfig,
    [UserRole.PARTNER]: ROLE_CONFIGS[UserRole.PARTNER] as PermissionsConfig,
    [UserRole.ASSOCIATE]: ROLE_CONFIGS[UserRole.ASSOCIATE] as PermissionsConfig,
    [UserRole.ASSISTANT]: ROLE_CONFIGS[UserRole.ASSISTANT] as PermissionsConfig,
  }
};

// ============================================================================
// UTILITY FUNCTIONS - Helper functions for working with permissions
// ============================================================================

/**
 * Checks if a role has a specific permission
 *
 * @param {Platform} platform - The platform (XTND or LYDECKER)
 * @param {UserRole} role - The user role to check
 * @param {string} subject - The permission subject (route or feature identifier)
 * @param {Action} action - The action to check (default: MANAGE)
 * @returns {boolean} True if the role has the permission, false otherwise
 *
 * @example
 * ```typescript
 * // Check if BILLING_ADMIN can access AR Collections reporting
 * const canAccess = hasPermission(
 *   Platform.XTND,
 *   UserRole.BILLING_ADMIN,
 *   PERMISSION_SUBJECT.ROUTES.AR_COLLECTIONS.REPORTING
 * );
 * ```
 */
export const hasPermission = (
  platform: Platform,
  role: UserRole,
  subject: string,
  action: Action = Action.MANAGE
): boolean => {
  const rolePermissions = PERMISSION[platform]?.[role];
  if (!rolePermissions) return false;

  // Type guard: check if it has router/features (not XTND/LYDECKER structure)
  const permissions = 'router' in rolePermissions && 'features' in rolePermissions
    ? rolePermissions
    : null;

  if (!permissions) return false;

  const allPermissions = [
    ...(permissions.router || []),
    ...(permissions.features || [])
  ];

  return allPermissions.some(
    perm => (perm.subject === subject || perm.subject === 'all') &&
      perm.action === action
  );
};

/**
 * Gets all permissions for a specific role on a platform
 *
 * @param {Platform} platform - The platform (XTND or LYDECKER)
 * @param {UserRole} role - The user role to get permissions for
 * @returns {Object} Object containing router and features permissions arrays
 *
 * @example
 * ```typescript
 * // Get all permissions for BILLING_ADMIN on XTND platform
 * const permissions = getRolePermissions(Platform.XTND, UserRole.BILLING_ADMIN);
 * // Returns: { router: [...], features: [...] }
 * ```
 */
export const getRolePermissions = (
  platform: Platform,
  role: UserRole
): PermissionsConfig => {
  const rolePermissions = PERMISSION[platform]?.[role];

  // Type guard: check if it has router/features (not XTND/LYDECKER structure)
  if (rolePermissions && 'router' in rolePermissions && 'features' in rolePermissions) {
    return rolePermissions;
  }

  return { router: [], features: [] };
};

/**
 * Gets the default route to redirect users after login based on platform and role
 *
 * This function determines the appropriate landing page for users after authentication.
 * The routing logic varies by platform and user role to ensure users are directed
 * to the most relevant section of the application.
 *
 * @param {Platform} platform - The platform (XTND or LYDECKER)
 * @param {UserRole} role - The user role to determine the default route for
 * @returns {string} The default route path to redirect to after login
 *
 * @example
 * ```typescript
 * Get default route for XTND platform
 * const route = getDefaultRouterLoggedInCallback(Platform.XTND, UserRole.ADMIN);
 * Returns: JUNIPER_ROUTES.TRANSACTIONS
 *
 * Get default route for Lydecker Billing Admin
 * const route = getDefaultRouterLoggedInCallback(Platform.LYDECKER, UserRole.BILLING_ADMIN);
 * Returns: AR_COLLECTIONS_ROUTES.AR.COLLECTIONS
 *
 * Get default route for Lydecker HR Standard
 * const route = getDefaultRouterLoggedInCallback(Platform.LYDECKER, UserRole.HR_STANDARD);
 * Returns: AR_COLLECTIONS_ROUTES.AR.REPORTING?tab=performance
 * ```
 *
 * @remarks
 * - XTND platform: Always redirects to Juniper Transactions
 * - LYDECKER platform:
 *   - Roles with queue access (BILLING_ADMIN, BOOKKEEPER, SUPERVISOR, HR_ADMIN, ADMIN, ACCOUNTING_ADMIN, CLERK): Redirect to Collections Queue
 *   - HR_STANDARD: Redirect to Reporting with Performance tab
 * - Fallback: Returns "/" for any unrecognized platform/role combination
 */
export const getDefaultRouterLoggedInCallback = (platform: Platform, role: UserRole): string => {
  if (platform === Platform.XTND) {
    return JUNIPER_ROUTES.TRANSACTIONS
  }

  if (platform === Platform.LYDECKER) {
    // Default for queue access routes
    if ([UserRole.BILLING_ADMIN, UserRole.BOOKKEEPER, UserRole.SUPERVISOR, UserRole.HR_ADMIN, UserRole.ADMIN, UserRole.ACCOUNTING_ADMIN, UserRole.CLERK].includes(role)) {
      return `${AR_COLLECTIONS_ROUTES.AR.COLLECTIONS}`;
    }

    // Default for Attorney roles (Partner, Associate, Assistant) - Ledger Reports
    if ([UserRole.PARTNER, UserRole.ASSOCIATE, UserRole.ASSISTANT].includes(role)) {
      return `${AR_COLLECTIONS_ROUTES.AR.LEDGER_REPORTS}`;
    }

    // Default for HR Standard role
    return `${AR_COLLECTIONS_ROUTES.AR.PERFORMANCE}`;
  }

  return "/";
}

/**
 * Gets the default reporting tab to display based on platform and role
 *
 * This function determines which tab should be shown by default in the AR Collections
 * Reporting section. Different roles have different default tabs based on their
 * primary responsibilities and access levels.
 *
 * @param {Platform} platform - The platform (XTND or LYDECKER)
 * @param {UserRole} role - The user role to determine the default tab for
 * @returns {string} The default tab identifier ('performance', 'email-queue', etc.)
 *
 * @example
 * ```typescript
 * Get default tab for XTND platform
 * const tab = getDefaultReportingTab(Platform.XTND, UserRole.ADMIN);
 * Returns: 'performance'
 *
 * Get default tab for Lydecker Billing Admin
 * const tab = getDefaultReportingTab(Platform.LYDECKER, UserRole.BILLING_ADMIN);
 * Returns: 'email-queue'
 *
 * Get default tab for Lydecker Accounting Admin
 * const tab = getDefaultReportingTab(Platform.LYDECKER, UserRole.ACCOUNTING_ADMIN);
 * Returns: 'performance'
 * ```
 *
 * @remarks
 * - XTND platform: Always returns 'performance'
 * - LYDECKER platform:
 *   - CLERK, BILLING_ADMIN, BOOKKEEPER: Default to 'email-queue'
 *   - ACCOUNTING_ADMIN, SUPERVISOR, ADMIN, HR_ADMIN, HR_STANDARD: Default to 'performance'
 *   - Fallback: Returns 'email-queue' for any unrecognized role
 * - Default fallback: Returns 'performance' for any unrecognized platform
 */
export const getDefaultReportingTab = (platform: Platform, role: UserRole): string => {
  if (platform === Platform.XTND) {
    return 'performance';
  }

  if (platform === Platform.LYDECKER) {
    if ([UserRole.CLERK, UserRole.BILLING_ADMIN, UserRole.BOOKKEEPER].includes(role)) {
      return 'email-queue';
    }

    if ([UserRole.ACCOUNTING_ADMIN, UserRole.SUPERVISOR, UserRole.ADMIN, UserRole.HR_ADMIN, UserRole.HR_STANDARD].includes(role)) {
      return 'performance';
    }

    if ([UserRole.PARTNER, UserRole.ASSOCIATE, UserRole.ASSISTANT].includes(role)) {
      return 'ledger-reports';
    }

    return 'email-queue';
  }

  return 'performance';
}
